/**
 * stateInterpolator — Application state interpolation for 20Hz → 60+ FPS client rendering.
 *
 * Interpolates entity positions, hp, and facing angle between two server snapshots
 * (snapA and snapB) based on renderTime.
 *
 * Application layer only (no DOM / Three.js).
 *
 * Contrato público:
 *   interpolateSnapshots(snapA, snapB, renderTime) → InterpolatedSnapshot
 */

/**
 * Linearly interpolates number `a` to `b` by factor `t` [0, 1].
 */
function lerp(a, b, t) {
  return a + (b - a) * t;
}

/**
 * Shortest path angle interpolation (radians).
 */
function lerpAngle(a, b, t) {
  let diff = (b - a) % (2 * Math.PI);
  if (diff < -Math.PI) diff += 2 * Math.PI;
  if (diff > Math.PI) diff -= 2 * Math.PI;
  return a + diff * t;
}

/**
 * Interpolates entity states between snapA and snapB for renderTime.
 * @param {object} snapA - Older snapshot
 * @param {object} snapB - Newer snapshot
 * @param {number} renderTime - Target render timestamp
 * @returns {object} interpolated snapshot
 */
export function interpolateSnapshots(snapA, snapB, renderTime) {
  if (!snapA && !snapB) return null;
  if (!snapA) return snapB;
  if (!snapB) return snapA;

  const timeA = snapA.serverTime || 0;
  const timeB = snapB.serverTime || timeA + 50;

  if (renderTime <= timeA) return snapA;
  if (renderTime >= timeB) return snapB;

  const totalTime = timeB - timeA;
  const t = totalTime > 0 ? (renderTime - timeA) / totalTime : 1;

  const mapB = new Map((snapB.entities || []).map((e) => [e.id || e.ownerId, e]));
  const interpolatedEntities = [];

  for (const entityA of snapA.entities || []) {
    const entityId = entityA.id || entityA.ownerId;
    const entityB = mapB.get(entityId);
    if (!entityB) {
      // Entity destroyed in snapB -> show snapA state
      interpolatedEntities.push({ ...entityA });
    } else {
      // Interpolate position and numerical fields
      interpolatedEntities.push({
        ...entityB,
        id: entityId,
        ownerId: entityA.ownerId || entityB.ownerId,
        x: lerp(entityA.x, entityB.x, t),
        z: lerp(
          entityA.z !== undefined ? entityA.z : (entityA.y || 0),
          entityB.z !== undefined ? entityB.z : (entityB.y || 0),
          t
        ),
        hp: Math.round(lerp(entityA.hp || 0, entityB.hp || 0, t)),
        facing: lerpAngle(entityA.facing || 0, entityB.facing || 0, t)
      });
    }
  }

  return {
    tick: snapB.tick,
    serverTime: renderTime,
    entities: interpolatedEntities,
    projectiles: Array.isArray(snapB.projectiles)
      ? snapB.projectiles.map((p) => ({ ...p }))
      : (Array.isArray(snapA.projectiles) ? snapA.projectiles.map((p) => ({ ...p })) : [])
  };
}
